import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { useAuth } from '@entities/auth';
import { useBalance } from '@features/balance';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';

function getInitials(source?: string): string {
  if (!source) return '?';

  const localPart = source.split('@')[0] ?? '';
  const words = localPart.split(/[\s._-]+/).filter(Boolean);

  if (words.length > 1) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  return localPart.slice(0, 2).toUpperCase() || '?';
}

interface UserMenuProps {
  className?: string;
}

export function UserMenu({ className }: UserMenuProps) {
  const { t } = useTranslation('cabinet');
  const { user, logout } = useAuth();
  const { balance } = useBalance();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex size-tap shrink-0 items-center justify-center rounded-xl bg-secondary text-sm font-medium text-foreground',
          'transition-colors duration-fast ease-standard hover:bg-accent',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          className
        )}
        aria-label={t('header.profile')}
      >
        {getInitials(user?.fullName || user?.email)}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <div className="px-2 pb-2 pt-1">
          <p className="truncate text-sm font-medium text-foreground">{user?.email}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('balance.available')}: {balance?.availableBalance == null ? t('balance.amountUnknown') : formatLedgerAmount(balance.availableBalance)}
          </p>
        </div>

        <DropdownMenuItem
          onSelect={() => void logout()}
          className="min-h-tap cursor-pointer gap-2 text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          {t('header.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
